import { useState, useEffect } from 'react';
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from '@easyflow/ui-master/components/ui/glass-card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { Label } from '@easyflow/ui-master/components/ui/label';
import { Textarea } from '@easyflow/ui-master/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-master/components/ui/tabs';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Switch } from '@easyflow/ui-master/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-master/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@easyflow/ui-master/components/ui/dialog';
import { Shield, Plus, Trash2, Save, Rocket, Loader2, Eye, Lock, AlertTriangle } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { toErr } from '@easyflow/shared';

interface Filter {
  id: number;
  name: string;
  description: string;
  filter_type: 'caller_id' | 'destination' | 'ip';
  condition_type: 'regex' | 'equals' | 'starts_with' | 'contains';
  pattern: string;
  action: 'block' | 'allow';
  priority: number;
  enabled: boolean;
  is_system: boolean;
  blocked_count: number;
  created_at: string;
}

export default function PublicContextEditor() {
  const { toast } = useToast();
  
  const [filters, setFilters] = useState<Filter[]>([]);
  const [previewXml, setPreviewXml] = useState('');
  const [activeTab, setActiveTab] = useState('filters');
  const [loading, setLoading] = useState(true);
  const [deploying, setDeploying] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newFilter, setNewFilter] = useState<Pick<Filter, 'name' | 'description' | 'filter_type' | 'condition_type' | 'pattern' | 'action' | 'priority'>>({
    name: '',
    description: '',
    filter_type: 'caller_id',
    condition_type: 'regex',
    pattern: '',
    action: 'block',
    priority: 100
  });

  useEffect(() => {
    void loadFilters();
  }, []);

  const loadFilters = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/master/dialplan/filters');
      const data = response.data as { filters: Filter[] };
      setFilters(data.filters || []);
    } catch (error: unknown) {
      const __error = toErr(error);
      console.error('Error loading filters:', error);
      toast({
        title: "Errore",
        description: __error.message || "Impossibile caricare i filtri",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadPreview = async () => {
    try {
      const response = await apiClient.post('/master/dialplan/filters/generate-xml');
      const data = response.data as { xml: string };
      setPreviewXml(data.xml || '');
    } catch (error: unknown) {
      const __error = toErr(error);
      toast({
        title: "Errore",
        description: __error.message || "Impossibile generare preview",
        variant: "destructive"
      });
    }
  };

  const handleToggleFilter = async (filter: Filter) => {
    try {
      await apiClient.put(`/master/dialplan/filters/${filter.id}`, {
        enabled: !filter.enabled
      });
      await loadFilters();
      toast({
        title: "Successo",
        description: `Filtro ${filter.enabled ? 'disabilitato' : 'abilitato'}`
      });
    } catch (error: unknown) {
      const __error = toErr(error);
      toast({
        title: "Errore",
        description: __error.message,
        variant: "destructive"
      });
    }
  };

  const handleAddFilter = async () => {
    try {
      if (!newFilter.name || !newFilter.pattern) {
        toast({
          title: "Errore",
          description: "Nome e pattern sono obbligatori",
          variant: "destructive"
        });
        return;
      }

      await apiClient.post('/master/dialplan/filters', newFilter);
      await loadFilters();
      setAddDialogOpen(false);
      setNewFilter({
        name: '',
        description: '',
        filter_type: 'caller_id',
        condition_type: 'regex',
        pattern: '',
        action: 'block',
        priority: 100
      });
      toast({
        title: "Successo",
        description: "Filtro aggiunto con successo"
      });
    } catch (error: unknown) {
      const __error = toErr(error);
      toast({
        title: "Errore",
        description: __error.message,
        variant: "destructive"
      });
    }
  };

  const handleDeleteFilter = async (filter: Filter) => {
    if (filter.is_system) {
      toast({
        title: "Errore",
        description: "I filtri di sistema non possono essere eliminati",
        variant: "destructive"
      });
      return;
    }

    try {
      await apiClient.delete(`/master/dialplan/filters/${filter.id}`);
      await loadFilters();
      toast({
        title: "Successo",
        description: "Filtro eliminato"
      });
    } catch (error: unknown) {
      const __error = toErr(error);
      toast({
        title: "Errore",
        description: __error.message,
        variant: "destructive"
      });
    }
  };

  const handleDeploy = async () => {
    try {
      setDeploying(true);
      await apiClient.post('/master/dialplan/filters/deploy');
      toast({
        title: "Successo",
        description: "Filtri deployati su FreeSWITCH",
        duration: 5000
      });
    } catch (error: unknown) {
      const __error = toErr(error);
      toast({
        title: "Errore",
        description: __error.message || "Deploy fallito",
        variant: "destructive"
      });
    } finally {
      setDeploying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const allowFilters = filters.filter(f => f.action === 'allow');
  const blockFilters = filters.filter(f => f.action === 'block');

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Shield className="h-8 w-8 text-red-500" />
            Anti-Fraud Filter Manager
          </h1>
          <p className="text-muted-foreground">
            Gestisci i filtri anti-frode per le chiamate in ingresso
          </p>
        </div>
        <div className="flex gap-2">
          <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Aggiungi Filtro
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Nuovo Filtro Anti-Frode</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Nome</Label>
                  <Input
                    value={newFilter.name}
                    onChange={(e) => setNewFilter({ ...newFilter, name: e.target.value })}
                    placeholder="es. Blocca numeri privati"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Descrizione</Label>
                  <Input
                    value={newFilter.description}
                    onChange={(e) => setNewFilter({ ...newFilter, description: e.target.value })}
                    placeholder="Descrizione opzionale"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo Filtro</Label>
                    <Select
                      value={newFilter.filter_type}
                      onValueChange={(v) => setNewFilter({ ...newFilter, filter_type: v as Filter['filter_type'] })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="caller_id">Caller ID</SelectItem>
                        <SelectItem value="destination">Destinazione</SelectItem>
                        <SelectItem value="ip">Indirizzo IP</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Condizione</Label>
                    <Select
                      value={newFilter.condition_type}
                      onValueChange={(v) => setNewFilter({ ...newFilter, condition_type: v as Filter['condition_type'] })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="regex">Regex</SelectItem>
                        <SelectItem value="equals">Uguale a</SelectItem>
                        <SelectItem value="starts_with">Inizia con</SelectItem>
                        <SelectItem value="contains">Contiene</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Pattern</Label>
                  <Input
                    value={newFilter.pattern}
                    onChange={(e) => setNewFilter({ ...newFilter, pattern: e.target.value })}
                    placeholder="es. ^[0-9]{1,3}$"
                    className="font-mono"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Azione</Label>
                    <Select
                      value={newFilter.action}
                      onValueChange={(v) => setNewFilter({ ...newFilter, action: v as Filter['action'] })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="block">Blocca</SelectItem>
                        <SelectItem value="allow">Permetti</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Priorita</Label>
                    <Input
                      type="number"
                      value={newFilter.priority}
                      onChange={(e) => setNewFilter({ ...newFilter, priority: parseInt(e.target.value) || 100 })}
                    />
                  </div>
                </div>
                <Button onClick={handleAddFilter} className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Aggiungi Filtro
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Button 
            onClick={handleDeploy} 
            disabled={deploying}
            className="bg-green-600 hover:bg-green-700"
          >
            {deploying ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Deploying...
              </>
            ) : (
              <>
                <Rocket className="h-4 w-4 mr-2" />
                Deploy su FreeSWITCH
              </>
            )}
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === 'preview') void loadPreview(); }}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="filters">
            Filtri Attivi
            <Badge variant="secondary" className="ml-2">{filters.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="preview">
            <Eye className="h-4 w-4 mr-1" />
            Preview XML
          </TabsTrigger>
          <TabsTrigger value="info">
            Info Sistema
          </TabsTrigger>
        </TabsList>

        <TabsContent value="filters" className="space-y-4">
          <GlassCard>
            <GlassCardHeader>
              <GlassCardTitle className="text-green-600">Whitelist (Permetti)</GlassCardTitle>
              <p className="text-sm text-muted-foreground">
                Questi pattern sono sempre permessi e bypassano i filtri di blocco
              </p>
            </GlassCardHeader>
            <GlassCardContent>
              <div className="space-y-2">
                {allowFilters.length === 0 ? (
                  <p className="text-muted-foreground text-sm">Nessun filtro whitelist</p>
                ) : (
                  allowFilters.map((filter) => (
                    <FilterRow 
                      key={filter.id} 
                      filter={filter} 
                      onToggle={handleToggleFilter}
                      onDelete={handleDeleteFilter}
                    />
                  ))
                )}
              </div>
            </GlassCardContent>
          </GlassCard>

          <GlassCard>
            <GlassCardHeader>
              <GlassCardTitle className="text-red-600">Blacklist (Blocca)</GlassCardTitle>
              <p className="text-sm text-muted-foreground">
                Questi pattern vengono bloccati immediatamente
              </p>
            </GlassCardHeader>
            <GlassCardContent>
              <div className="space-y-2">
                {blockFilters.length === 0 ? (
                  <p className="text-muted-foreground text-sm">Nessun filtro blacklist</p>
                ) : (
                  blockFilters.map((filter) => (
                    <FilterRow 
                      key={filter.id} 
                      filter={filter} 
                      onToggle={handleToggleFilter}
                      onDelete={handleDeleteFilter}
                    />
                  ))
                )}
              </div>
            </GlassCardContent>
          </GlassCard>
        </TabsContent>

        <TabsContent value="preview">
          <GlassCard>
            <GlassCardHeader>
              <GlassCardTitle>Preview public.xml</GlassCardTitle>
              <p className="text-sm text-muted-foreground">
                Questo XML verra generato e deployato su FreeSWITCH
              </p>
            </GlassCardHeader>
            <GlassCardContent>
              <div className="bg-gray-900 rounded-lg p-4 overflow-auto max-h-[600px]">
                <pre className="text-green-400 text-xs font-mono">
                  <code>{previewXml || 'Caricamento...'}</code>
                </pre>
              </div>
            </GlassCardContent>
          </GlassCard>
        </TabsContent>

        <TabsContent value="info">
          <GlassCard>
            <GlassCardHeader>
              <GlassCardTitle>Come Funziona</GlassCardTitle>
            </GlassCardHeader>
            <GlassCardContent className="space-y-4">
              <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">Sistema a Doppio Stadio</h3>
                <ol className="list-decimal list-inside text-sm text-blue-700 dark:text-blue-400 space-y-1">
                  <li><strong>Stadio 1 (public.xml):</strong> Filtri statici per bloccare caller ID fraudolenti</li>
                  <li><strong>Stadio 2 (Backend):</strong> Validazione DID dinamica dal database</li>
                </ol>
              </div>
              
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <h3 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-2 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Attenzione
                </h3>
                <ul className="list-disc list-inside text-sm text-yellow-700 dark:text-yellow-400 space-y-1">
                  <li>I filtri di sistema (con lucchetto) non possono essere eliminati</li>
                  <li>Le modifiche diventano attive solo dopo il Deploy</li>
                  <li>Il Deploy crea automaticamente un backup del file precedente</li>
                </ul>
              </div>

              <div className="p-4 bg-muted/50 dark:bg-gray-800/50 border border-border dark:border-gray-700 rounded-lg">
                <h3 className="font-semibold text-foreground dark:text-foreground mb-2">Tipi di Filtro</h3>
                <ul className="text-sm text-muted-foreground dark:text-muted-foreground/70 space-y-1">
                  <li><strong>Caller ID:</strong> Filtra in base al numero chiamante</li>
                  <li><strong>Destinazione:</strong> Filtra in base al numero chiamato</li>
                  <li><strong>IP:</strong> Filtra in base all indirizzo IP origine</li>
                </ul>
              </div>
            </GlassCardContent>
          </GlassCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function FilterRow({ 
  filter, 
  onToggle, 
  onDelete 
}: { 
  filter: Filter; 
  onToggle: (f: Filter) => void;
  onDelete: (f: Filter) => void;
}) {
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border ${
      filter.enabled ? 'bg-white dark:bg-gray-800 border-border dark:border-gray-700' : 'bg-muted/50 dark:bg-gray-900 border-gray-100 dark:border-gray-800 opacity-60'
    }`}>
      <div className="flex items-center gap-3">
        <Switch 
          checked={filter.enabled} 
          onCheckedChange={() => onToggle(filter)}
        />
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium">{filter.name}</span>
            {filter.is_system && (
              <span title="Filtro di sistema">
                <Lock className="h-3 w-3 text-muted-foreground/70" />
              </span>
            )}
            <Badge variant="outline" className="text-xs">
              {filter.filter_type}
            </Badge>
            <Badge variant="outline" className="text-xs">
              P:{filter.priority}
            </Badge>
          </div>
          <code className="text-xs text-muted-foreground dark:text-muted-foreground/70 font-mono">{filter.pattern}</code>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {filter.blocked_count > 0 && (
          <Badge variant="secondary">{filter.blocked_count} bloccate</Badge>
        )}
        {!filter.is_system && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onDelete(filter)}
            className="text-red-500 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
